import {
  ApiError,
  CenteredLoader,
  ChartCoordinates,
  LineChart,
  LineChartDataset,
  LineChartProps,
  TooltipItem,
  useTheme,
} from "@shared/primitive-components"
import { lightenColor } from "@shared/primitive-components/src/utils/lightenDarken"
import { useMemo } from "react"
import { useInsights, usePolicyAndFleet } from "../../../contexts"
import { reduceDateByOneHour, toChartDate } from "../../../utils"
import { ChartPeriod, mapPeriodsToChart } from "./helper"
import { usePolicyAccessCapabilities } from "../../../hooks"
import { useRebateSummary } from "../../../api/rebates/useGetRebateSummary"

export const MILEAGE_CHART_DTI = `mileage_chart`

type MileageChartProps = {
  periods: ReadonlyArray<ChartPeriod>
  isRebateV2Policy: boolean
}

const Chart = ({ periods, isRebateV2Policy }: MileageChartProps): JSX.Element => {
  const { policy } = usePolicyAndFleet()
  const { startTime } = policy
  const theme = useTheme()
  const chart = useMemo<LineChartProps<ChartCoordinates<number | null>[], string>>(() => {
    // Build datasets
    const labels = [toChartDate(startTime), ...periods.map((period) => toChartDate(reduceDateByOneHour(period.date)))]
    const mileageData = periods?.map((it, i) => ({
      x: labels[i + 1],
      y: it.mileage,
    }))
    const periodsDatesets = periods?.map((it, i) => ({
      x: labels[i + 1],
      y: it.expectedMileage,
    }))
    periodsDatesets.unshift({ x: toChartDate(startTime), y: 0 })
    mileageData.unshift({ x: toChartDate(startTime), y: 0 })
    const datasets: LineChartDataset<ChartCoordinates<number | null>[]> = [
      {
        data: mileageData,
        datasetOptions: { order: 2, borderColor: theme.palette.brand, pointRadius: 4 },
        useGradient: true,
      },
    ]
    if (!isRebateV2Policy) {
      datasets.push({
        data: periodsDatesets,
        datasetOptions: { order: 1, borderDash: [5, 5], pointRadius: 4 },
      })
    }

    // Build Tooltips
    const toolTipLabelFn = (tooltipItem: TooltipItem<"line">): string[] => {
      const { raw } = tooltipItem
      const coordinate = raw as ChartCoordinates<number>
      const wording = tooltipItem.datasetIndex === 1 ? `Estimate` : `Accumulated`
      if (tooltipItem.datasetIndex === 1) {
        return [`${wording}: ${coordinate.y.toLocaleString()}mi`]
      }

      return [`${wording}: ${coordinate.y.toLocaleString()}mi`]
    }

    const toolTipTitleFn = (tooltipItem: TooltipItem<"line">[]): string => {
      const item = tooltipItem[0]
      const { raw } = item
      const { x } = raw as ChartCoordinates<number>
      return x
    }

    const hasPeriods = periods.length > 0
    const isNullMileageForV2 = hasPeriods && isRebateV2Policy && periods[0].mileage === null
    const isNullExpectedMileageForNonV2 = hasPeriods && !isRebateV2Policy && periods[0].expectedMileage === null

    const yAxisMaxValue = !hasPeriods || isNullMileageForV2 || isNullExpectedMileageForNonV2 ? 10000 : undefined

    return {
      datasets,
      gradientColor: lightenColor(theme.palette.brand, 100),
      height: 360,
      labels,
      maintainAspectRatio: false,
      responsive: true,
      toolTipLabelFn,
      toolTipTitleFn,
      yAxisMaxValue,
    }
  }, [startTime, periods, theme.palette.brand, isRebateV2Policy])
  return <LineChart<ChartCoordinates<number | null>[], string> chart={chart} dataTestId={MILEAGE_CHART_DTI} />
}

const MileageChart = () => {
  const { isRebateV2Policy } = usePolicyAccessCapabilities()
  const { apiErrored: errRebates, data: summary, loading: rebatesLoading } = useRebateSummary()
  const { periods } = useInsights()

  const mileageChartPeriods = mapPeriodsToChart(
    isRebateV2Policy ? periods?.periods : summary?.periods,
    isRebateV2Policy
  )
  if (errRebates) {
    return <ApiError />
  }

  if (rebatesLoading) {
    return <CenteredLoader height="100%" />
  }

  return <Chart periods={mileageChartPeriods} isRebateV2Policy={isRebateV2Policy} />
}

export const MileageChartComponent = <MileageChart />
